import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Link } from 'react-router-dom';

import './stylesheet.css';

import * as baseActions from '../../actions/base';

import ChoiceModal from '../UI/ChoiceModal';

import { parseOrg } from '../../lib/parse_org';
import { dashboardConfigFromState, filePathToUrl } from '../../lib/dashboard';
import {
  collectTasks,
  dailyFoldersForWindow,
  dailyPathsForWindow,
  orderTasksForPicker,
  WEEK_WINDOW_DAYS,
} from '../../lib/week_dashboard';
import {
  appendSession,
  bestRun,
  currentRun,
  DEFAULT_FOCUS_MINUTES,
  dayStrip,
  FOCUS_DURATIONS_MINUTES,
  FOCUS_STRIP_DAYS,
  formatClock,
  isSessionElapsed,
  normalizeFocusLog,
  OUTCOME_ABANDONED,
  OUTCOME_COMPLETED,
  OUTCOME_EARLY,
  pauseSession,
  resumeSession,
  rewardFor,
  secondsOn,
  secondsRemaining,
  secondsSpent,
  sessionsOn,
  sessionToLogEntry,
  startSession,
} from '../../lib/focus';

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const FocusDashboard = ({ base, syncBackendClient, storedConfig, storedFocusLog }) => {
  const config = useMemo(() => dashboardConfigFromState(storedConfig), [storedConfig]);
  const focusLog = useMemo(() => normalizeFocusLog(storedFocusLog), [storedFocusLog]);

  const [tasks, setTasks] = useState(null);
  const [chosenTask, setChosenTask] = useState(null);
  const [minutes, setMinutes] = useState(DEFAULT_FOCUS_MINUTES);
  const [session, setSession] = useState(null);
  const [isPickingTask, setIsPickingTask] = useState(false);
  const [closing, setClosing] = useState(null);

  // Only forces a repaint: every duration is computed from session.startedAt against
  // the clock, so a throttled background tab cannot stretch the session.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!session || session.pausedAt) return undefined;

    const id = setInterval(() => setTick((value) => value + 1), 500);
    return () => clearInterval(id);
  }, [session]);

  const loadTasks = useCallback(async () => {
    if (!syncBackendClient) {
      setTasks([]);
      return;
    }

    const today = new Date();
    const expected = dailyPathsForWindow(config.dailyPathTemplate, today, WEEK_WINDOW_DAYS);

    try {
      const folders = dailyFoldersForWindow(config.dailyPathTemplate, today, WEEK_WINDOW_DAYS);
      const listings = await Promise.all(
        folders.map((folder) => syncBackendClient.getDirectoryListing(folder).catch(() => null))
      );

      const present = new Set();
      listings.forEach((listing) => {
        if (!listing) return;
        (listing.listing || []).forEach((file) => {
          const path = file.get ? file.get('path') : file.path;
          if (path) present.add(path);
        });
      });

      const wanted = [
        ...expected.filter(({ path }) => present.has(path)),
        ...(config.gtdPath ? [{ path: config.gtdPath, date: null }] : []),
      ];

      const collected = await Promise.all(
        wanted.map(async ({ path, date }) => {
          try {
            const parsed = parseOrg(await syncBackendClient.getFileContents(path));
            return collectTasks({
              headers: parsed.get('headers'),
              todoKeywordSets: parsed.get('todoKeywordSets'),
              path,
              date,
            }).open;
          } catch (error) {
            return [];
          }
        })
      );

      setTasks([].concat(...collected));
    } catch (error) {
      console.error('Could not read open tasks:', error);
      setTasks([]);
    }
  }, [config.dailyPathTemplate, config.gtdPath, syncBackendClient]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const now = Date.now();
  const today = useMemo(() => new Date(), []);

  const remaining = secondsRemaining(session, now);
  const elapsed = isSessionElapsed(session, now);

  const strip = useMemo(() => dayStrip(focusLog.sessions, today, FOCUS_STRIP_DAYS), [
    focusLog.sessions,
    today,
  ]);
  // Today's own tasks first, then the rest of the week newest-first, GTD last -
  // see lib/week_dashboard.js for the reasoning.
  const orderedTasks = useMemo(() => orderTasksForPicker(tasks, today), [tasks, today]);
  const todaysSessions = sessionsOn(focusLog.sessions, today).length;
  const todaysMinutes = Math.round(secondsOn(focusLog.sessions, today) / 60);
  const run = currentRun(focusLog.sessions, today);
  const best = bestRun(focusLog.sessions);
  const busiestDay = Math.max(1, ...strip.map((column) => column.sessions));

  // Ending the session is the closure step: nothing is logged until it is answered.
  const finish = (outcome) => {
    if (!session) return;

    setClosing({
      outcome,
      seconds: secondsSpent(session, Date.now()),
      taskText: session.taskText,
      path: session.path,
      entry: sessionToLogEntry(session, { now: Date.now(), outcome }),
    });
    setSession(null);
  };

  const commitClosing = () => {
    if (!closing) return;

    base.setFocusLog({
      sessions: appendSession(focusLog.sessions, closing.entry),
      rewards: focusLog.rewards,
    });
    setClosing(null);
  };

  // The clock running out ends the session by itself, so an unattended session still
  // reaches its closure step rather than sitting at 0:00.
  useEffect(() => {
    if (session && !session.pausedAt && isSessionElapsed(session, Date.now())) {
      finish(OUTCOME_COMPLETED);
    }
  }, [session, remaining]); // eslint-disable-line

  const header = (
    <div className="focus__header">
      <h3 className="focus__title">Focus</h3>
      <div className="focus__header__actions">
        <Link className="focus__header__action" to="/dashboard/contract">
          <i className="fas fa-file-signature" /> Contract
        </Link>
        <Link className="focus__header__action" to="/dashboard/week">
          <i className="fas fa-chart-line" /> This week
        </Link>
        <Link className="focus__header__action" to="/dashboard/team">
          <i className="fas fa-users" /> Team
        </Link>
        <Link className="focus__header__action" to="/dashboard/formation">
          <i className="fas fa-seedling" /> Formation
        </Link>
        <Link className="focus__header__action" to="/dashboard/review">
          <i className="fas fa-moon" /> Review
        </Link>
        <Link className="focus__header__action" to="/dashboard">
          <i className="fas fa-th-large" /> Dashboard
        </Link>
      </div>
    </div>
  );

  // ── Closure ───────────────────────────────────────────────────────────────
  if (closing) {
    const reward = rewardFor(focusLog.rewards, todaysSessions);
    const spentMinutes = Math.max(1, Math.round(closing.seconds / 60));

    return (
      <div className="focus-container">
        {header}
        <div className="focus__card">
          <div className="focus__card__headline">
            {closing.outcome === OUTCOME_ABANDONED
              ? `Stopped after ${spentMinutes} min`
              : `${spentMinutes} min on`}
          </div>
          <div className="focus__card__task">{closing.taskText || '(untitled)'}</div>

          {closing.outcome !== OUTCOME_ABANDONED && !!reward && (
            <div className="focus__reward">{reward}</div>
          )}

          <div className="focus__card__detail">
            {closing.outcome === OUTCOME_ABANDONED
              ? 'Logged. Starting again is the only thing that matters.'
              : `That is ${todaysSessions + 1} today${run > 0 ? `, and a ${run}-day run` : ''}.`}
          </div>

          <div className="focus__card__actions">
            {!!closing.path && (
              <Link className="btn-passive" to={filePathToUrl(closing.path)} onClick={commitClosing}>
                <i className="fas fa-file-alt" /> Open the file
              </Link>
            )}
            <button className="btn" onClick={commitClosing}>
              <i className="fas fa-check" /> Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Running ───────────────────────────────────────────────────────────────
  if (session) {
    const total = session.seconds;
    const fraction = total > 0 ? Math.min(1, (total - remaining) / total) : 0;

    return (
      <div className="focus-container">
        {header}

        <div className="focus__stage">
          <div
            className="focus__ring"
            style={{
              // A conic gradient is the cheapest honest progress ring: no SVG, no library.
              background: `conic-gradient(var(--magenta) ${Math.round(fraction * 360)}deg, var(--base2) 0deg)`,
            }}
          >
            <div className="focus__ring__inner">
              <div className="focus__clock">{formatClock(remaining)}</div>
              {!!session.pausedAt && <div className="focus__paused">paused</div>}
            </div>
          </div>

          <div className="focus__stage__task">
            <div className="focus__stage__task__text">{session.taskText || '(untitled)'}</div>
            {!!session.path && <div className="focus__stage__task__path">{session.path}</div>}
          </div>
        </div>

        <div className="focus__controls">
          {session.pausedAt ? (
            <button className="btn-passive" onClick={() => setSession(resumeSession(session, Date.now()))}>
              <i className="fas fa-play" /> Resume
            </button>
          ) : (
            <button className="btn-passive" onClick={() => setSession(pauseSession(session, Date.now()))}>
              <i className="fas fa-pause" /> Pause
            </button>
          )}
          {/* Finishing early is a success: the task is the goal, not the clock. */}
          <button className="btn" onClick={() => finish(elapsed ? OUTCOME_COMPLETED : OUTCOME_EARLY)}>
            <i className="fas fa-check" /> Done
          </button>
          <button className="btn-passive" onClick={() => finish(OUTCOME_ABANDONED)}>
            <i className="fas fa-times" /> Give up
          </button>
        </div>
      </div>
    );
  }

  // ── Idle: pre-commitment ──────────────────────────────────────────────────
  return (
    <div className="focus-container">
      {header}

      {isPickingTask && (
        <ChoiceModal
          title="What are you working on?"
          description={`Open tasks from the last ${WEEK_WINDOW_DAYS} days and ${config.gtdPath}.`}
          isBusy={tasks === null}
          busyMessage="Reading your open tasks..."
          choices={orderedTasks.map((task) => ({
            key: task.key,
            label: task.text || '(untitled)',
            description: task.path,
            icon: task.kind === 'headline' ? 'tasks' : 'check-square',
          }))}
          onChoose={(key) => {
            setChosenTask(orderedTasks.find((task) => task.key === key) || null);
            setIsPickingTask(false);
          }}
          onCancel={() => setIsPickingTask(false)}
        />
      )}

      <div className="focus__section-title">What are you working on?</div>
      <button className="focus__chosen" onClick={() => setIsPickingTask(true)}>
        {chosenTask ? (
          <>
            <span className="focus__chosen__text">{chosenTask.text || '(untitled)'}</span>
            <span className="focus__chosen__path">{chosenTask.path}</span>
          </>
        ) : (
          <span className="focus__chosen__empty">
            {tasks === null
              ? 'Reading your open tasks...'
              : tasks.length === 0
              ? 'No open tasks found — tap to retry'
              : 'Choose a task'}
          </span>
        )}
      </button>

      <div className="focus__section-title">For how long?</div>
      <div className="focus__durations">
        {FOCUS_DURATIONS_MINUTES.map((option) => (
          <button
            className={`focus__duration${option === minutes ? ' focus__duration--selected' : ''}`}
            key={option}
            onClick={() => setMinutes(option)}
          >
            {option}
            <span className="focus__duration__unit">min</span>
          </button>
        ))}
      </div>

      <button
        className="focus__start"
        disabled={!chosenTask}
        onClick={() => setSession(startSession({ task: chosenTask, minutes, now: Date.now() }))}
      >
        <i className="fas fa-play" /> Start focusing
      </button>

      {/* The whole gamification surface: a record and two personal bests. */}
      <div className="focus__section-title">Your record</div>
      <div className="focus__strip">
        {strip.map((column) => (
          <div className="focus__strip__day" key={column.date.toDateString()}>
            <div className="focus__strip__bar-track">
              <div
                className="focus__strip__bar"
                style={{ height: `${Math.round((column.sessions / busiestDay) * 100)}%` }}
              />
            </div>
            <div className="focus__strip__label">{WEEKDAY_INITIALS[column.date.getDay()]}</div>
          </div>
        ))}
      </div>

      <div className="focus__stats">
        <div className="focus__stat">
          <strong>{todaysSessions}</strong> today
        </div>
        <div className="focus__stat">
          <strong>{todaysMinutes}</strong> min
        </div>
        <div className="focus__stat">
          <strong>{run}</strong> day run
        </div>
        <div className="focus__stat">
          <strong>{best}</strong> best
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  syncBackendClient: state.syncBackend.get('client'),
  storedConfig: state.base.get('dashboardConfig'),
  storedFocusLog: state.base.get('focusLog'),
});

const mapDispatchToProps = (dispatch) => ({
  base: bindActionCreators(baseActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(FocusDashboard);
